import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Upload, Check } from "lucide-react";
import {
  crystalTypeOptions,
  focalOptions,
  colorOptions,
  formatPrice,
} from "../data/products";

export default function LensConfigurator({ open, onOpenChange, product, onAdd }) {
  const [crystals, setCrystals] = useState(["organico"]);
  const [focal, setFocal] = useState("mono-focal");
  const [color, setColor] = useState("sin-color");
  const [receta, setReceta] = useState("no");
  const [fileName, setFileName] = useState("");
  const [fileUrl, setFileUrl] = useState("");

  const reset = () => {
    setCrystals(["organico"]);
    setFocal("mono-focal");
    setColor("sin-color");
    setReceta("no");
    setFileName("");
    setFileUrl("");
  };

  useEffect(() => {
    if (open) reset();
  }, [open, product?.id]);

  // Multi-select: el cliente puede elegir 1 o varios cristales.
  // Color habilitado solo si entre los seleccionados está "fotocromatico".
  const toggleCrystal = (val) => {
    setCrystals((prev) => {
      const has = prev.includes(val);
      if (has) {
        // no permitir quedar sin ninguno
        return prev.length === 1 ? prev : prev.filter((v) => v !== val);
      }
      return [...prev, val];
    });
  };

  const colorsDisabled = !crystals.includes("fotocromatico");

useEffect(() => {
  if (colorsDisabled && color !== "sin-color") {
    setColor("sin-color");
  }
}, [colorsDisabled, color]);

  // Subir archivo al backend cuando el cliente lo selecciona
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads`, { method: "POST", body: fd });
      if (r.ok) {
        const data = await r.json();
        setFileUrl(data.url);
      }
    } catch {
      // silencio: si falla la subida aún avisamos por el nombre
    }
  };

  const handleAdd = () => {
    const crystalLabels = crystals.map((v) => crystalTypeOptions.find((c) => c.value === v)?.label).filter(Boolean).join(" + ");
    onAdd({
      productId: product?.id,
      productName: product?.name,
      crystal: crystalLabels,
      focal: focalOptions.find((f) => f.value === focal)?.label,
      color: colorOptions.find((c) => c.value === color)?.label,
      receta: receta === "si" ? "Sí" : "No",
      hasFile: !!fileName,
      fileName,
      fileUrl,
    });
    reset();
    onOpenChange(false);
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-[#FAF9F6] border border-[#EAEAEA] rounded-none p-0 max-h-[90vh] overflow-y-auto"
        data-testid="lens-configurator-dialog"
      >
        <div className="grid md:grid-cols-5 gap-0">
          <div className="hidden md:block md:col-span-2 product-image-wrap aspect-auto">
            <img src={product.image} alt={product.alt} />
          </div>

          <div className="md:col-span-3 p-8 md:p-10">
            <DialogHeader className="space-y-2 text-left">
              <span className="eyebrow">Cotiza tu lente</span>
              <DialogTitle className="font-serif-display text-3xl leading-tight text-[#1C1C1C]">
                {product.name}
              </DialogTitle>
              <DialogDescription className="text-[#595959] text-sm leading-relaxed">
                Marco {formatPrice(product.price)} · incluye estuche y paño.
                Configura cristales y servicios para completar tu cotización.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-8 space-y-6">
              <div>
                <span className="eyebrow block mb-3">Tipo de cristal <span className="text-[#9A9A9A] normal-case tracking-normal text-[10px]">(puedes elegir varios)</span></span>
                <div className="flex flex-wrap gap-2">
                  {crystalTypeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleCrystal(opt.value)}
                      className={`chip ${crystals.includes(opt.value) ? "active" : ""}`}
                      data-testid={`crystal-option-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="eyebrow block mb-3">Tipo focal</span>
                <div className="flex flex-wrap gap-2">
                  {focalOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFocal(opt.value)}
                      className={`chip ${focal === opt.value ? "active" : ""}`}
                      data-testid={`focal-option-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="eyebrow block mb-3">
                  Color {colorsDisabled && <span className="text-[#9A9A9A] normal-case tracking-normal text-[10px]">(solo disponible con fotocromático)</span>}
                </span>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={colorsDisabled && opt.value !== "sin-color"}
                      onClick={() => setColor(opt.value)}
                      className={`chip ${color === opt.value ? "active" : ""} ${colorsDisabled && opt.value !== "sin-color" ? "opacity-30 cursor-not-allowed" : ""}`}
                      data-testid={`color-option-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="eyebrow block mb-3">¿Tienes receta?</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setReceta("si")} className={`chip ${receta === "si" ? "active" : ""}`} data-testid="receta-si">Sí</button>
                  <button type="button" onClick={() => setReceta("no")} className={`chip ${receta === "no" ? "active" : ""}`} data-testid="receta-no">No</button>
                </div>
              </div>

              {receta === "si" && (
                <div>
                  <span className="eyebrow block mb-3">Adjuntar receta (opcional)</span>
                  <label htmlFor="receta-file" className="flex items-center justify-between gap-3 border border-dashed border-[#EAEAEA] px-4 py-3 cursor-pointer hover:border-[#1C1C1C] transition-colors">
                    <div className="flex items-center gap-3 text-sm text-[#595959]">
                      {fileName ? (<><Check className="w-4 h-4 text-[#1C1C1C]" /><span className="text-[#1C1C1C] truncate max-w-[200px]">{fileName}</span></>)
                        : (<><Upload className="w-4 h-4" /><span>Subir archivo (PDF, JPG, PNG)</span></>)}
                    </div>
                    <span className="eyebrow">Seleccionar</span>
                    <input id="receta-file" type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} data-testid="receta-file-input" />
                  </label>
                </div>
              )}
            </div>

            <DialogFooter className="mt-10 flex sm:justify-start">
              <button type="button" onClick={handleAdd} className="btn-primary w-full sm:w-auto" data-testid="add-to-cart-confirm">
                Agregar al carrito
              </button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
